/**
 * App Initialization Service
 * Handles app-wide initialization tasks like preloading poems for Persian loader
 */

import { poemCacheService } from './poemCacheService';

class AppInitService {
  private static instance: AppInitService;
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;

  private constructor() {}

  public static getInstance(): AppInitService {
    if (!AppInitService.instance) {
      AppInitService.instance = new AppInitService();
    }
    return AppInitService.instance;
  }

  /**
   * Initialize the application
   * This should be called once when the app starts
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('AppInit: Already initialized');
      return;
    }

    if (this.initPromise) {
      console.log('AppInit: Already initializing, waiting...');
      return this.initPromise;
    }

    console.log('AppInit: Starting app initialization...');
    this.initPromise = this.performInitialization();

    try {
      await this.initPromise;
      this.isInitialized = true;
      console.log('AppInit: App initialization completed successfully');
    } catch (error) {
      console.error('AppInit: App initialization failed:', error);
      // Don't throw error to prevent app from breaking
    } finally {
      this.initPromise = null;
    }
  }

  /**
   * Check if app is initialized
   */
  public isAppInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get initialization status
   */
  public async getInitStatus(): Promise<{ 
    initialized: boolean; 
    cacheStats: any; 
    loggingStats?: any;
  }> {
    const status: { 
      initialized: boolean; 
      cacheStats: any; 
      loggingStats?: any;
    } = {
      initialized: this.isInitialized,
      cacheStats: poemCacheService.getCacheStats()
    };

    // Add logging statistics if available
    try {
      const { apiService } = await import('./api');
      const { globalErrorHandler } = await import('./globalErrorHandler');
      
      if (apiService.isLoggingInitialized()) {
        status.loggingStats = {
          apiLogging: await apiService.getLoggingStatistics(),
          globalErrorHandler: globalErrorHandler.getStatistics()
        };
      }
    } catch (error) {
      console.warn('AppInit: Failed to get logging statistics:', error);
    }

    return status;
  }

  /**
   * Perform the actual initialization tasks
   */
  private async performInitialization(): Promise<void> {
    const tasks = [
      this.initializePoemCache(),
      this.initializeApiLogging(),
      // Add other initialization tasks here in the future
    ];

    // Run all initialization tasks in parallel
    await Promise.allSettled(tasks);
  }

  /**
   * Initialize poem cache for Persian loader
   */
  private async initializePoemCache(): Promise<void> {
    try {
      console.log('AppInit: Initializing poem cache...');
      
      // Check if we already have cached poems
      if (poemCacheService.hasCachedPoems()) {
        console.log('AppInit: Poems already cached, skipping API call');
        return;
      }

      // Preload poems from the public API
      await poemCacheService.getPoems();
      const stats = poemCacheService.getCacheStats();
      
      console.log('AppInit: Poem cache initialized successfully:', {
        totalPoems: stats.totalPoems,
        activePoems: stats.activePoems,
        cached: stats.cached
      });
    } catch (error) {
      console.error('AppInit: Failed to initialize poem cache:', error);
      // Don't throw error - app should work with fallback poems
    }
  }

  /**
   * Initialize API logging system
   */
  private async initializeApiLogging(): Promise<void> {
    try {
      console.log('AppInit: Initializing API logging system...');
      
      // Initialize API logging through the API service
      const { apiService } = await import('./api');
      await apiService.initializeLogging();
      
      // Also initialize other logging services
      const { userActionLogger } = await import('./userActionLogger');
      const { databaseLogger } = await import('./databaseLogger');
      const { globalErrorHandler } = await import('./globalErrorHandler');
      
      userActionLogger.initialize();
      databaseLogger.initialize();
      globalErrorHandler.initialize();
      
      console.log('AppInit: API logging system initialized successfully');
    } catch (error) {
      console.error('AppInit: Failed to initialize API logging system:', error);
      // Don't throw error - app should work without logging
    }
  }

  /**
   * Refresh all caches (useful for admin actions)
   */
  public async refreshCaches(): Promise<void> {
    try {
      console.log('AppInit: Refreshing all caches...');
      await poemCacheService.refreshCache();
      console.log('AppInit: All caches refreshed successfully');
    } catch (error) {
      console.error('AppInit: Failed to refresh caches:', error);
    }
  }
}

// Export singleton instance
export const appInitService = AppInitService.getInstance();